import React, { useMemo, useState } from 'react';
import { CheckIcon, CloudIcon, PencilIcon, PlusIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Meter } from '../ui/Meter';
import { courses as seedCourses } from '../../data/academics';
import type { Course } from '../../types/academic';
const MAX_UNITS = 24;
const MIN_UNITS = 15;
export function RegistrationPanel() {
  const [courses, setCourses] = useState<Course[]>(seedCourses);
  const registered = useMemo(() => courses.filter(course => course.status === 'registered'), [courses]);
  const available = useMemo(() => courses.filter(course => course.status !== 'registered'), [courses]);
  const units = registered.reduce((total, course) => total + course.units, 0);
  function toggle(id: string) {
    setCourses(current => current.map(course => course.id === id ? {
      ...course,
      status: course.status === 'registered' ? 'available' : 'registered'
    } : course));
  }
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-294" data-mp="35xjdoo327575">
      <div className="lg:col-span-2" data-id="element-295" data-mp="39yd1lhgi4k01">
        <Panel title="Registered courses" description="These feed your timetable, assignments and attendance." bodyClassName="p-0" data-id="element-296" data-mp="39xabvcux0nlp">
          <ul className="divide-y divide-line" data-id="element-297" data-mp="3joeoi5x4bwdd">
            {registered.map(course => <li key={course.id} className="flex items-start gap-4 px-5 py-4" data-id="element-298" data-mp="3on99neik5x0n">
                <div className="min-w-0 flex-1" data-id="element-299" data-mp="3op2f4qhfqb72">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-300" data-mp="3tndpd2bn6npt">
                    <p className="cz-num text-sm font-semibold text-ink" data-id="element-301" data-mp="1i9txbpe2vbp">{course.code}</p>
                    <p className="text-sm text-ink" data-id="element-302" data-mp="1z8b4jhxjftt8">{course.title}</p>
                  </div>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-muted" data-id="element-303" data-mp="1zanrk9i5kud5">
                    <span className="cz-num" data-id="element-304" data-mp="36kk8jvhiux1e">{course.units} units</span>
                    <span data-id="element-305" data-mp="36mwo61zt4vba">{course.lecturer}</span>
                    <span className="inline-flex items-center gap-1" data-id="element-306" data-mp="31mirth7kj9t3">
                      {course.source === 'synced' ? <>
                          <CloudIcon className="h-3.5 w-3.5" data-id="element-307" data-mp="3fj5nlgnsbtxf" />
                          From university directory
                        </> : <>
                          <PencilIcon className="h-3.5 w-3.5" data-id="element-308" data-mp="3phbnxtty0s06" />
                          Added by you
                        </>}
                    </span>
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => toggle(course.id)} data-id="element-309" data-mp="3uamxi2q53mvs">
                  Remove
                </Button>
              </li>)}
          </ul>
        </Panel>
      </div>

      <div className="space-y-6" data-id="element-310" data-mp="76737qltxrpm">
        <Panel title="Registration load" data-id="element-311" data-mp="76zz1wj92wsz">
          <Meter value={units / MAX_UNITS * 100} tone={units < MIN_UNITS ? 'warn' : 'brand'} label="Units this semester" trailing={`${units} / ${MAX_UNITS}`} data-id="element-312" data-mp="qrh25pniekje" />
          <p className="cz-num mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-313" data-mp="hrbudeeelk4p">
            Minimum {MIN_UNITS} units, maximum {MAX_UNITS}.{' '}
            {units < MIN_UNITS ? `You need ${MIN_UNITS - units} more units before registration closes.` : 'Your load is within the allowed range.'}
          </p>
        </Panel>

        <Panel title="Add a course" description="Directory courses for your programme." data-id="element-314" data-mp="ctaet3g6s2xz">
          <ul className="space-y-3" data-id="element-315" data-mp="3u9iikn71l3l6">
            {available.map(course => <li key={course.id} className="flex items-start justify-between gap-3" data-id="element-316" data-mp="32xth90js5qs">
                <div className="min-w-0" data-id="element-317" data-mp="3l6zi29qqb323">
                  <p className="cz-num text-[13px] font-semibold text-ink" data-id="element-318" data-mp="3l8at8umi7d38">{course.code}</p>
                  <p className="truncate text-[13px] text-ink-muted" data-id="element-319" data-mp="3q62nhvamfbal">{course.title}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-320" data-mp="3i4l7c9ssmjnw">
                    {course.units} units · {course.enrolled}/{course.capacity} enrolled
                  </p>
                </div>
                {course.status === 'closed' ? <Badge tone="warn" data-id="element-321" data-mp="34ergjlxq9pbj">Full</Badge> : <Button size="sm" variant="secondary" icon={<PlusIcon className="h-3.5 w-3.5" data-id="element-323" data-mp="2zdbym2qujyxd" />} onClick={() => toggle(course.id)} data-id="element-322" data-mp="34cf0jebj5p14">
                    Add
                  </Button>}
              </li>)}
          </ul>
          <p className="mt-4 flex items-start gap-2 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-324" data-mp="2lgf7e06tgffy">
            <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-325" data-mp="nokoybcbiqou" />
            Registering here records your own plan. It does not submit registration to the university
            until they enable full integration.
          </p>
        </Panel>
      </div>
    </div>;
}